const REFILL_THRESHOLD = 5;
const MAX_BUFFERED_TASKS = 30;
const playable = (task) => Boolean(task?.id && task.videoUrl && task.status !== 'removed' && !task.archived
  && !['deleting', 'deleted'].includes(task.mediaState) && !task.mediaDeletedAt);

// This queue deliberately uses only the staff listing endpoint. Public review
// assignments mix sections and carry volunteer vote/progress semantics.
export function createAdminReviewQueue({ api, filter, autoFlaggedDirection = 'all' }) {
  let snapshot = { tasks: [], loading: false, error: null, exhausted: false };
  let cursor = null;
  let generation = 0;
  let disposed = false;
  let inFlight = null;
  let cursorBlocked = false;
  const listeners = new Set();
  const handled = new Set();
  const requestedCursors = new Set();
  const sources = new Map();
  const refreshRequests = new Map();

  const optionsFor = (sourceCursor) => ({
    filter,
    ...(filter === 'autoFlagged' && autoFlaggedDirection !== 'all' ? { autoFlaggedDirection } : {}),
    ...(sourceCursor ? { cursor: sourceCursor } : {}),
    includeStats: false,
  });
  const publish = (changes) => {
    if (disposed || Object.entries(changes).every(([key, value]) => snapshot[key] === value)) return;
    snapshot = { ...snapshot, ...changes };
    for (const listener of listeners) {
      try { listener(snapshot); } catch (_) { /* A viewer cannot invalidate a successful page load. */ }
    }
  };
  const current = (version) => !disposed && generation === version;

  function load() {
    if (disposed) return Promise.resolve(snapshot);
    if (inFlight) return inFlight;
    // Each staff page contains 25 rows. Wait until another page fits instead of
    // discarding clips or retaining an ever-growing history of signed URLs.
    if (snapshot.exhausted || cursorBlocked || snapshot.tasks.length > REFILL_THRESHOLD) return Promise.resolve(snapshot);
    const version = generation;
    const sourceCursor = cursor;
    const request = Promise.resolve().then(async () => {
      if (!current(version)) return snapshot;
      try {
        const result = await api.admin(optionsFor(sourceCursor));
        if (!current(version)) return snapshot;
        const nextCursor = result.nextCursor || null;
        const requested = new Set(requestedCursors);
        requested.add(sourceCursor);
        if (nextCursor && requested.has(nextCursor)) {
          cursorBlocked = true;
          const error = new Error('The review list repeated a page. Refresh review mode to continue.');
          error.code = 'review/pagination-loop';
          throw error;
        }
        const present = new Set(snapshot.tasks.map((task) => task.id));
        const additions = [];
        for (const task of result.tasks || []) {
          if (!playable(task) || handled.has(task.id) || present.has(task.id)) continue;
          present.add(task.id);
          additions.push(task);
        }
        if (snapshot.tasks.length + additions.length > MAX_BUFFERED_TASKS) {
          throw new Error('Finish the current clips before loading another review batch.');
        }
        requestedCursors.add(sourceCursor);
        for (const task of additions) sources.set(task.id, sourceCursor);
        cursor = nextCursor;
        publish({
          tasks: additions.length ? [...snapshot.tasks, ...additions] : snapshot.tasks,
          loading: false,
          error: null,
          exhausted: !nextCursor,
        });
      } catch (error) {
        if (current(version)) publish({ loading: false, error });
      }
      return snapshot;
    }).finally(() => { if (current(version) && inFlight === request) inFlight = null; });
    inFlight = request;
    publish({ loading: true, error: null });
    return request;
  }

  function remove(taskId) {
    if (disposed || !taskId) return;
    handled.add(taskId);
    sources.delete(taskId);
    refreshRequests.delete(taskId);
    const tasks = snapshot.tasks.filter((task) => task.id !== taskId);
    if (tasks.length !== snapshot.tasks.length) publish({ tasks });
  }

  function refresh() {
    if (disposed) return Promise.resolve(snapshot);
    generation += 1;
    inFlight = null;
    cursor = null;
    cursorBlocked = false;
    requestedCursors.clear();
    sources.clear();
    refreshRequests.clear();
    publish({ tasks: [], loading: false, error: null, exhausted: false });
    return load();
  }

  function refreshTask(taskId) {
    if (disposed || handled.has(taskId) || !sources.has(taskId)) return Promise.resolve(null);
    if (refreshRequests.has(taskId)) return refreshRequests.get(taskId);
    const version = generation;
    const sourceCursor = sources.get(taskId);
    const request = Promise.resolve().then(async () => {
      if (!current(version) || handled.has(taskId) || !sources.has(taskId)) return null;
      try {
        const result = await api.admin(optionsFor(sourceCursor));
        if (!current(version) || handled.has(taskId) || !sources.has(taskId)) return null;
        const renewed = (result.tasks || []).find((task) => task.id === taskId && playable(task)) || null;
        if (!renewed) {
          sources.delete(taskId);
          publish({ tasks: snapshot.tasks.filter((task) => task.id !== taskId), error: null });
          return null;
        }
        publish({ tasks: snapshot.tasks.map((task) => task.id === taskId ? renewed : task), error: null });
        return renewed;
      } catch (error) {
        if (!current(version) || handled.has(taskId) || !sources.has(taskId)) return null;
        publish({ error });
        throw error;
      }
    }).finally(() => {
      if (current(version) && refreshRequests.get(taskId) === request) refreshRequests.delete(taskId);
    });
    refreshRequests.set(taskId, request);
    return request;
  }

  return {
    subscribe(listener) {
      if (disposed) return () => {};
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    load,
    remove,
    refresh,
    refreshTask,
    dispose() {
      disposed = true;
      generation += 1;
      listeners.clear();
      sources.clear();
      refreshRequests.clear();
      handled.clear();
      requestedCursors.clear();
      inFlight = null;
    },
  };
}
